import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Header from "../components/header";
import Footer from "../components/footer";
import Loader from "../components/loader";
import { blogPosts } from "../../utils/blogData";
import { getBlogContent } from "../../utils/blogContent";
import { blogService } from "../../services/blogService";

const BlogDetail = () => {
  const [loading, setLoading] = useState(true);
  const { slug } = useParams();
  const navigate = useNavigate();
  const [readingProgress, setReadingProgress] = useState(0);
  const [selectedSection, setSelectedSection] = useState("all");
  const [apiPost, setApiPost] = useState(null);

  // Check if it's a hardcoded blog first
  const hardcodedPost = blogPosts.find((post) => post.slug === slug);

  // The current post is either hardcoded or from API
  const currentPost = hardcodedPost || apiPost;

  // Get latest posts for sidebar (excluding current post)
  const latestPosts = blogPosts
    .filter((post) => post.slug !== slug)
    .slice(0, 5);

  useEffect(() => {
    // If not a hardcoded post, try to fetch from API
    if (!hardcodedPost) {
      loadApiPost();
    } else {
      finishLoading();
    }
  }, [slug]);

  const loadApiPost = async () => {
    try {
      const data = await blogService.getBlogBySlug(slug);
      if (data) {
        setApiPost({
          id: data._id || data.id,
          slug: data.slug,
          title: data.title,
          summary: data.excerpt || data.summary || "",
          image: data.featuredImage || data.image || "",
          category: data.categories?.[0]?.name || data.categories?.[0] || data.category || "general",
          date: data.publishDate
            ? new Date(data.publishDate).toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })
            : "",
          readTime: data.readingTime || data.readTime || 5,
          content: data.content || "",
          fromApi: true,
        });
        finishLoading();
      } else {
        navigate("/blog");
      }
    } catch (error) {
      console.error("Error loading blog post:", error);
      navigate("/blog");
    }
  };

  const finishLoading = () => {
    if (currentPost || hardcodedPost) {
      const post = hardcodedPost || currentPost;
      document.title = `${post?.title || ""} - TAK8 Car Rental Blog`;

      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription && post) {
        metaDescription.setAttribute("content", post.summary);
      }
    }

    window.scrollTo({ top: 0, behavior: "instant" });
    setTimeout(() => setLoading(false), 500);
  };

  useEffect(() => {
    if (!currentPost) return;

    // Reading progress tracking
    const handleScroll = () => {
      const scrolled = window.scrollY;
      const maxHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (scrolled / maxHeight) * 100;
      setReadingProgress(Math.min(progress, 100));
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    // Set up intersection observer for table of contents
    const observer = new IntersectionObserver(
      (entries) => {
        let visibleEntries = entries.filter((entry) => entry.isIntersecting);
        if (visibleEntries.length > 0) {
          visibleEntries.reduce((prev, current) => {
            return prev.boundingClientRect.top < current.boundingClientRect.top
              ? prev
              : current;
          });
        }
      },
      { rootMargin: "-20% 0px -70% 0px", threshold: [0, 0.1, 0.5, 1.0] }
    );

    setTimeout(() => {
      const headings = document.querySelectorAll(
        ".BlogContent h2[id], .BlogContent h3[id]"
      );
      headings.forEach((heading) => observer.observe(heading));
    }, 1000);

    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", handleScroll);
    };
  }, [currentPost]);

  // Anchor navigation
  useEffect(() => {
    const scrollToElement = (targetId) => {
      const element = document.getElementById(targetId);
      if (element) {
        const headerOffset = 80;
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
        window.scrollTo({ top: offsetPosition, behavior: "smooth" });
      }
    };

    const handleAnchorClick = (e) => {
      const target = e.target.closest("a");
      if (target && target.getAttribute("href")?.startsWith("#")) {
        e.preventDefault();
        const targetId = target.getAttribute("href").substring(1);
        window.history.replaceState(null, null, `#${targetId}`);
        scrollToElement(targetId);
      }
    };

    const handleHashChange = () => {
      const hash = window.location.hash.substring(1);
      if (hash) {
        setTimeout(() => scrollToElement(hash), 100);
      }
    };

    if (window.location.hash) {
      setTimeout(() => handleHashChange(), 100);
    }

    document.addEventListener("click", handleAnchorClick);
    window.addEventListener("hashchange", handleHashChange);

    return () => {
      document.removeEventListener("click", handleAnchorClick);
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, []);

  const handleBackToList = () => {
    sessionStorage.setItem("shouldRestoreScroll", "true");
    navigate("/blog");
  };

  const handlePostClick = (postSlug) => {
    sessionStorage.removeItem("blogListScrollPosition");
    sessionStorage.removeItem("shouldRestoreScroll");
    navigate(`/blog/${postSlug}`);
  };

  // Get full content - hardcoded or API
  const getFullContent = (post) => {
    if (!post) return "";
    // If it's a hardcoded post, use blogContent.js
    if (!post.fromApi) {
      const contentData = getBlogContent(post.slug);
      return contentData?.content || "";
    }
    // If it's an API post, use the content field directly
    return post.content || "";
  };

  // Generate table of contents from full content
  const generateTableOfContents = () => {
    const content = getFullContent(currentPost);
    const headings = content.match(/<h[23][^>]*>([^<]*)<\/h[23]>/gi) || [];

    return headings.map((heading, index) => {
      const level = heading.match(/<h([23])/)[1];
      const text = heading.replace(/<[^>]*>/g, "").trim();
      const id = text
        .toLowerCase()
        .replace(/[^a-z0-9\s]/g, "")
        .replace(/\s+/g, "-")
        .replace(/^-|-$/g, "");

      return { id, text, level: parseInt(level), index: index + 1 };
    });
  };

  // Add IDs to headings in the content
  const addIdsToContent = (content) => {
    if (!content) return "";

    // Remove the first image from content since we display it separately
    let processedContent = content.replace(
      /(<div class="blog-content-wrapper">\s*)<img[^>]*>/,
      "$1"
    );

    return processedContent.replace(
      /<h([23])([^>]*)>([^<]*)<\/h[23]>/gi,
      (match, level, attrs, text) => {
        const cleanText = text.trim();
        const id = cleanText
          .toLowerCase()
          .replace(/[^a-z0-9\s]/g, "")
          .replace(/\s+/g, "-")
          .replace(/^-|-$/g, "");
        return `<h${level}${attrs} id="${id}">${cleanText}</h${level}>`;
      }
    );
  };

  // Extract specific section content
  const extractSectionContent = (content, sectionId) => {
    if (!content || selectedSection === "all") return addIdsToContent(content);

    const wrapperMatch = content.match(
      /<div class="blog-content-wrapper">([\s\S]*)<\/div>/
    );
    const innerContent = wrapperMatch ? wrapperMatch[1] : content;

    const sections = innerContent.split(/(<h2[^>]*>.*?<\/h2>)/gi);
    let sectionContent = "";
    let capturing = false;
    let targetSectionFound = false;

    for (let i = 0; i < sections.length; i++) {
      const section = sections[i];
      const h2HeadingMatch = section.match(/<h2[^>]*>([^<]*)<\/h2>/gi);

      if (h2HeadingMatch) {
        const text = section.replace(/<[^>]*>/g, "").trim();
        const id = text
          .toLowerCase()
          .replace(/[^a-z0-9\s]/g, "")
          .replace(/\s+/g, "-")
          .replace(/^-|-$/g, "");

        if (id === sectionId) {
          capturing = true;
          targetSectionFound = true;
          sectionContent += section;
        } else if (capturing && targetSectionFound) {
          break;
        }
      } else if (capturing) {
        sectionContent += section;
      }
    }

    if (!targetSectionFound) {
      const allSections = innerContent.split(/(<h[23][^>]*>.*?<\/h[23]>)/gi);
      let foundTargetH3 = false;
      let currentH2Content = "";

      for (let i = 0; i < allSections.length; i++) {
        const section = allSections[i];
        const h2Match = section.match(/<h2[^>]*>([^<]*)<\/h2>/gi);

        if (h2Match) {
          if (foundTargetH3) break;
          currentH2Content = section;
        } else {
          const h3Match = section.match(/<h3[^>]*>([^<]*)<\/h3>/gi);
          if (h3Match) {
            const text = section.replace(/<[^>]*>/g, "").trim();
            const id = text
              .toLowerCase()
              .replace(/[^a-z0-9\s]/g, "")
              .replace(/\s+/g, "-")
              .replace(/^-|-$/g, "");

            if (id === sectionId) {
              foundTargetH3 = true;
              sectionContent = currentH2Content + section;
            } else if (foundTargetH3) {
              break;
            }
          } else if (foundTargetH3) {
            sectionContent += section;
          } else {
            currentH2Content += section;
          }
        }
      }
    }

    const finalContent = sectionContent.trim()
      ? `<div class="blog-content-wrapper">${sectionContent}</div>`
      : addIdsToContent(content);

    return addIdsToContent(finalContent);
  };

  const handleSectionClick = (id) => {
    setSelectedSection(id);
    setTimeout(() => {
      const contentElement = document.querySelector(".BlogContent");
      if (contentElement) {
        const offset = contentElement.offsetTop - 100;
        window.scrollTo({ top: offset, behavior: "smooth" });
      }
    }, 100);
  };

  if (loading) {
    return <Loader />;
  }

  if (!currentPost) {
    return null;
  }

  const tableOfContents = generateTableOfContents();

  return (
    <>
      <Header />

      {/* Reading Progress Bar */}
      <div className="reading-progress" style={{ width: `${readingProgress}%` }}></div>

      <section className="BlogDetailSection">
        <div className="container">
          {/* Back to Blog Button */}
          <div className="BackToBlog">
            <button onClick={handleBackToList}>
              <i className="fas fa-arrow-left"></i>
              Back to Blog
            </button>
          </div>

          {/* Main Blog Image */}
          <div className="BlogMainImage">
            <img src={currentPost.image} alt={currentPost.title} />
            <div className="CategoryBadge">{currentPost.category}</div>
          </div>

          {/* Blog Header */}
          <div className="BlogDetailHeader">
            <h1>{currentPost.title}</h1>

            <div className="BlogMeta">
              <div className="meta-item">
                <i className="fas fa-calendar"></i>
                <span>{currentPost.date}</span>
              </div>
              <div className="meta-item">
                <i className="fas fa-clock"></i>
                <span>{currentPost.readTime} min read</span>
              </div>
              <div className="meta-item">
                <i className="fas fa-user"></i>
                <span>TAK8 Team</span>
              </div>
            </div>
          </div>

          <div className="row">
            {/* Main Content */}
            <div className="col-lg-8">
              <div className="BlogContent">
                <div
                  dangerouslySetInnerHTML={{
                    __html: extractSectionContent(
                      getFullContent(currentPost),
                      selectedSection
                    ),
                  }}
                />
              </div>
            </div>

            {/* Sidebar */}
            <div className="col-lg-4">
              <div className="blog-sidebar-container">
                {/* Table of Contents */}
                {tableOfContents.length > 0 && (
                  <div className="TableOfContents">
                    <h4>
                      <i className="fas fa-list"></i>
                      Table of Contents
                    </h4>

                    <ul>
                      {tableOfContents.map((item) => (
                        <li key={item.id}>
                          <a
                            href={`#${item.id}`}
                            onClick={(e) => {
                              e.preventDefault();
                              handleSectionClick(item.id);
                            }}
                            className={selectedSection === item.id ? "active" : ""}
                            style={{
                              paddingLeft: item.level === 3 ? "20px" : "0px",
                            }}
                          >
                            {item.text}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Latest Blogs */}
                <div className="LatestBlogs">
                  <h4>
                    <i className="fas fa-newspaper"></i>
                    Latest Articles
                  </h4>

                  {latestPosts.map((post) => (
                    <div
                      key={post.id}
                      className="LatestBlogItem"
                      onClick={() => handlePostClick(post.slug)}
                    >
                      <div className="LatestBlogImage">
                        <img src={post.image} alt={post.title} />
                      </div>
                      <div className="LatestBlogContent">
                        <div className="LatestBlogMeta">
                          <span className="CategoryBadge small">{post.category}</span>
                          <span className="ReadTime">
                            <i className="fas fa-clock"></i>
                            {post.readTime} min
                          </span>
                        </div>
                        <h6>{post.title}</h6>
                        <p className="BlogDate">
                          <i className="fas fa-calendar"></i>
                          {post.date}
                        </p>
                      </div>
                    </div>
                  ))}

                  <div className="ViewAllBlogs">
                    <button className="BtnBorder" onClick={() => navigate("/blog")}>
                      View All Articles
                      <i className="fas fa-arrow-right"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default BlogDetail;
